import { Injectable } from '@angular/core';
import { AnchorType, AvatarRegion, Campaign } from '../../../models';
import anchorsTypeList from './../../../../assets/jsons/anchors-type.json';
import { catchError, combineLatest, distinctUntilChanged, finalize, Observable, of, shareReplay, startWith, Subject, switchMap, tap } from 'rxjs';
import { MakingWavesLoaderService } from '../../shared/services';
import { AmazonS3Service } from './amazon-s3.service';
import { CreateCampaignApiService } from './create-campaign-api.service';

@Injectable({
  providedIn: 'root'
})
export class CreateCampaignLogicService {
  private onReadFileEnd$ = new Subject<string[]>();
  valuesFromFileAsText!: string;

  constructor(
    private apiService: CreateCampaignApiService,
    private loaderService: MakingWavesLoaderService,
    private amazonS3Service: AmazonS3Service
  ) { }

  public uploadFileToS3(file: File, queryName: string) {
    return this.amazonS3Service.uploadFile(file, queryName);
  }

  public postNewCampaign(campaign: Campaign) {
    return this.apiService.postNewCampaign(campaign);
  }

  public getAvatarsRegion$(): Observable<AvatarRegion[]> {
    return this.apiService.getAvatarsRegion$();
  }

  public getLabels() {
    return this.apiService.getLabels();
  }

  public getGroupsByLabelIds(labelIds: number[]) {
    return this.apiService.getGroupsByLabelIds(labelIds);
  }

  public getAnchorsTypeList(): AnchorType[] {
    return anchorsTypeList as AnchorType[];
  }

  public async getInputValuesFromFile(file: any): Promise<void> {
    try {
      const fileContent = await this.readFileAsync(file);
      await this.processDataInChunksAsync(fileContent);
    }
    catch (error) {
      console.error('Error processing data:', error);
    }
  }

  private readFileAsync(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const content = event.target?.result as string;
        resolve(content);
      };

      reader.onerror = (event) => {
        reject(event.target?.error);
      };

      reader.readAsText(file);
    });
  }

  private async processDataInChunksAsync(data: string): Promise<void> {
    const lines = data.split('\n');
    this.onReadFileEnd$.next(lines);
  }

  public getStringArrayValues$() {
    return this.onReadFileEnd$.asObservable();
  }

  public getGroupRegions$(groupId$: Observable<string | undefined>): Observable<string[]> {
    return groupId$.pipe(
      switchMap(groupId => {
        console.log(`groupId: ${groupId}`)
        if (!groupId) {
          return of([]);
        }
        return this.apiService.getGroupRegions$(groupId);
      })
    );
  }

  public getAvatarCount$(
    appId$: Observable<string | undefined>,
    ownerUnitId$: Observable<string | undefined>,
    groupId$: Observable<string | undefined>,
    resourceType$: Observable<'page' | 'group'>,
    regionId$: Observable<string | undefined>,
    gender$: Observable<string | undefined> = of(undefined),
    ageFrom$: Observable<number | undefined> = of(undefined),
    ageTo$: Observable<number | undefined> = of(undefined)
  ): Observable<number | null> {
    return combineLatest([
            appId$.pipe(distinctUntilChanged()),
            ownerUnitId$.pipe(distinctUntilChanged()),
            groupId$.pipe(distinctUntilChanged()),
            resourceType$.pipe(distinctUntilChanged()),
            regionId$.pipe(distinctUntilChanged()),
            gender$.pipe(distinctUntilChanged()),
            ageFrom$.pipe(distinctUntilChanged()),
            ageTo$.pipe(distinctUntilChanged())
          ]).pipe(
        switchMap(([appId, ownerId, groupId, resourceType, regionId, gender, ageFrom, ageTo]) => {
          if (appId && ownerId && groupId && regionId) {
            this.loaderService.showLoader("בודק מספר אווטארים בקבוצה...");
            return this.apiService.countAvatarsInGroup$(appId, groupId, ownerId, regionId, gender, ageFrom, ageTo, 72).pipe(
              catchError((exc) => {
                console.error("a call to count-avatars-in-group returned exception", exc);
                return of(-1);
              }),
              finalize(() => this.loaderService.hideLoader()),
              startWith(null)
            );
          } else if (appId && ownerId && regionId && resourceType == 'page') {
            this.loaderService.showLoader("בודק מספר אווטארים לאזור וליחידה ...");
            return this.apiService.countAvatars$(appId, ownerId, regionId, gender, ageFrom, ageTo).pipe(
              catchError((exc) => {
                console.error("a call to count-avatars returned exception", exc);
                return of(-1);
              }),
              finalize(() => this.loaderService.hideLoader()),
              startWith(null)
            );
          }
          return of(null);
        }),
        shareReplay(1)
      );
  }
}
